import { Button, Flex, Form, Modal, type ModalProps, Row, message } from 'antd';
import { FC, useEffect } from 'react';

import { useAdminCompanyController_updateOneById } from '@api-client/generated/AdminCompanyController/updateOneById';
import { Schemas } from '@api-client/generated/types';
import { CompanyDetailsWrapperFormControl } from '@entities';
import { useCurrentCompanyId } from '@hooks';
import { captureException } from '@sentry/react';
import { getApiErrorMessage } from '@utils';

import * as S from './styled';

const SENTRY_TAGS = { feature: 'company-contact-person' } as const;

type FormType = {
  details?: {
    contactPerson?: Schemas.ContactPersonDto;
  };
};

type CompanyContactPersonEditProps = {
  company: Schemas.AdminCompanyResponseDto;
  onCancel: () => void;
  onAfterEdit: () => void;
} & ModalProps;

/**
 * The client's contact person — the fifth field MDS has nowhere to put.
 *
 * MDS holds no contact-person record at all: `CompanyPersonRole.role_type` is free text with no
 * `contact` convention, and the legacy contact person survives in MDS only inside
 * `extended_attributes`, as provenance rebuilt from this database every tick. So this one keeps a
 * form here while the rest of the card is read-only. The client can also change it themselves in
 * the invoice generator, which is the other reason it is not MDS-owned yet.
 */
const CompanyContactPersonEdit: FC<CompanyContactPersonEditProps> = ({
  company,
  open,
  onAfterEdit,
  onCancel,
  ...rest
}) => {
  const companyId = useCurrentCompanyId();

  const [form] = Form.useForm<FormType>();

  const { mutate: updateCompany, isPending: loading } =
    useAdminCompanyController_updateOneById();

  useEffect(() => {
    form.setFieldsValue({
      details: { contactPerson: company.details?.contactPerson },
    });
  }, [company, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleSubmit = ({ details }: FormType) => {
    // The two names are `required` on the form, so antd does not reach here without them — this
    // narrows the type rather than guarding against a case the form allows.
    if (!details?.contactPerson) {
      return;
    }

    updateCompany(
      {
        parameter: {
          id: companyId!,
        },
        // Same rule as the accounting modal: `details` is one jsonb column, so the MDS-owned keys
        // are carried through, and no other column of the company is sent at all.
        requestBody: {
          details: {
            ...company.details,
            // The stored value first: `language` is not on this form and must survive it.
            contactPerson: {
              ...company.details?.contactPerson,
              ...details.contactPerson,
            },
          },
        },
      },
      {
        onSuccess: () => {
          message.success('Contact person has been successfully updated');

          onAfterEdit();
          handleCancel();
        },
        // The axios interceptor only handles a 401, so without this a rejected write left the
        // modal open with no explanation — and the client-facing lock made 403 a real answer here.
        // Captured as well as shown: a toast is gone in four seconds, and a write refused for a
        // reason nobody expected (a 403 naming a field, a 500) has to leave something behind.
        onError: (error) => {
          captureException(error, { level: 'error', tags: SENTRY_TAGS });

          return message.error(
            getApiErrorMessage(error, 'Could not update the contact person.')
          );
        },
      }
    );
  };

  return (
    <Modal
      width={575}
      open={open}
      title="Edit contact person"
      footer={null}
      onCancel={handleCancel}
      destroyOnClose
      centered
      {...rest}
    >
      <Form<FormType>
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
        requiredMark={false}
      >
        <S.Card>
          <Row gutter={[12, 0]}>
            <CompanyDetailsWrapperFormControl
              type="input"
              span={12}
              form={{
                label: 'First name',
                name: ['details', 'contactPerson', 'firstName'],
                rules: [{ required: true }],
              }}
            />

            <CompanyDetailsWrapperFormControl
              type="input"
              span={12}
              form={{
                label: 'Last name',
                name: ['details', 'contactPerson', 'lastName'],
                rules: [{ required: true }],
              }}
            />
          </Row>
        </S.Card>

        <S.Card>
          <CompanyDetailsWrapperFormControl
            type="input"
            form={{
              label: 'Email',
              name: ['details', 'contactPerson', 'email'],
              rules: [{ type: 'email' }],
            }}
          />

          <CompanyDetailsWrapperFormControl
            type="input"
            form={{
              label: 'Phone',
              name: ['details', 'contactPerson', 'phone'],
            }}
          />
        </S.Card>

        <S.Submit>
          <Form.Item noStyle>
            <Flex justify="flex-end" gap={12}>
              <Button onClick={handleCancel} size="large">
                Cancel
              </Button>

              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={loading}
              >
                Save
              </Button>
            </Flex>
          </Form.Item>
        </S.Submit>
      </Form>
    </Modal>
  );
};

export default CompanyContactPersonEdit;
